/**
 * Lobby sharing: the host's Invite card (player search + Copy invite link),
 * and the small share card (phones, and "Know someone?" for non-hosts of
 * public games). One button everywhere: the invite link carries the code. In
 * private games the host also sees the code, beside the button.
 */

import React from 'react'
import { View, Text } from 'react-native'
import * as Clipboard from 'expo-clipboard'
import type { GameResponse } from '@warcarp/api-types'
import { InvitePlayerSearch } from '../InvitePlayerSearch'
import { MiniAction } from './LobbySeatRow'
import { lobbyShareInfo } from '../../../lib/lobbySeats'
import { reportError } from '../../../lib/reporting'

/** A pill that copies `value` and briefly confirms. */
export function CopyAction({ label, value, accessibilityLabel, testID }: {
  label: string; value: string; accessibilityLabel: string; testID?: string
}) {
  const [copied, setCopied] = React.useState(false)
  React.useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(t)
  }, [copied])
  async function copy() {
    try {
      await Clipboard.setStringAsync(value)
      setCopied(true)
    } catch (err) {
      reportError(err, { tags: { area: 'lobby-copy' }, extra: { label } })
    }
  }
  return (
    <MiniAction
      label={copied ? '✓ Copied' : label}
      accessibilityLabel={copied ? `${accessibilityLabel}: copied` : accessibilityLabel}
      onPress={() => void copy()}
      testID={testID}
    />
  )
}

function inviteSets(game: GameResponse) {
  const pending = game.invitations.filter((i) => i.status === 'pending')
  return {
    existingUserIds: new Set(game.players.map((p) => p.userId)),
    invitedUserIds: new Set(pending.map((i) => i.user.id)),
    declinedUserIds: new Set(game.invitations.filter((i) => i.status === 'declined').map((i) => i.user.id)),
  }
}

/** The private game's code, shown to the host beside Copy invite link. */
function InviteCode({ code }: { code: string }) {
  return (
    <Text selectable testID="lobby-invite-code" accessibilityLabel={`Invite code ${code.split('').join(' ')}`}
      className="text-ink font-stencil text-base tracking-widest">{code}</Text>
  )
}

/** Host's Invite card: search and Copy invite link (private: the code beside it). */
export function LobbyInviteCard({ game, seatsOpen, autoFocus }: {
  game: GameResponse
  /** No open seat: search is replaced by a note (invites need a free seat). */
  seatsOpen: number
  autoFocus?: boolean
}) {
  const share = lobbyShareInfo(game)
  const sets = inviteSets(game)
  return (
    <View testID="lobby-invite-card" className="bg-paper border border-olive rounded-xl p-3 gap-3">
      <Text className="text-ink font-stencil text-sm uppercase tracking-widest" accessibilityRole="header">Invite</Text>
      <View className="flex-row items-start" style={{ gap: 12 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          {seatsOpen > 0 ? (
            <InvitePlayerSearch
              gameId={game.id}
              existingUserIds={sets.existingUserIds}
              invitedUserIds={sets.invitedUserIds}
              declinedUserIds={sets.declinedUserIds}
              placeholder="Search players by name…"
              autoFocus={autoFocus}
              bare
            />
          ) : (
            <Text className="text-ink-soft font-typed text-xs italic">
              Every seat is taken. Remove a player or add seats to invite someone.
            </Text>
          )}
        </View>
      </View>
      <View className="flex-row flex-wrap items-center" style={{ gap: 8 }}>
        {share.code && <InviteCode code={share.code} />}
        {share.link && <CopyAction label="Copy invite link" value={share.link} accessibilityLabel="Copy invite link" testID="lobby-copy-link" />}
        <Text className="text-ink-soft font-typed text-xs" style={{ flexShrink: 1 }}>{share.note}</Text>
      </View>
      {share.link && <Text selectable testID="invite-url" className="text-ink-soft font-typed text-[11px]">{share.link}</Text>}
    </View>
  )
}

/**
 * Small share card: phones (host) and non-hosts of public games ("Know
 * someone?").
 */
export function LobbyShareCard({ game, isHost }: { game: GameResponse; isHost: boolean }) {
  const share = lobbyShareInfo(game)
  if (!share.link) return null
  const code = isHost ? share.code : null
  const note = isHost
    ? share.note
    : 'Public game: anyone can join from the games list or with this link.'
  return (
    <View testID="lobby-share-card" className="bg-paper border border-olive rounded-xl px-3 py-2.5 gap-2">
      <View className="flex-row flex-wrap items-center" style={{ gap: 8 }}>
        {!isHost && <Text className="text-ink font-stencil text-xs uppercase tracking-widest">Know someone?</Text>}
        {code && <InviteCode code={code} />}
        <CopyAction label="Copy invite link" value={share.link} accessibilityLabel="Copy invite link" testID="lobby-copy-link" />
      </View>
      <Text className="text-ink-soft font-typed text-xs">{note}</Text>
    </View>
  )
}
